'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import {
  GameLayout,
  GameVictoryModal,
  GameRulesModal,
  DifficultySelector,
  useGameState,
  useGameTimer,
  useGameSettings
} from '@/contexts/games/components/common';
import { MemoryGame as MemoryGameType, MemoryGameSettings, Card } from './types/memory-game.types';
import { DEFAULT_GAME_SETTINGS, GameResult, GameDifficulty, DEFAULT_GAME_STATISTICS } from '@/contexts/games/types';
import { GameBoard } from './components/GameBoard';
import { useHintSystem } from './hooks/useHintSystem';
import { generateCards } from './utils/card-generator';
import { GAME_CONFIG, GAME_SETTINGS } from './utils/game-config';
import { Button } from '@/shared/components/ui/button';
import { Lightbulb } from 'lucide-react';

// Default settings for Memory Game
const MEMORY_DEFAULT_SETTINGS: MemoryGameSettings = {
  ...DEFAULT_GAME_SETTINGS,
  maxHints: GAME_SETTINGS.MAX_HINTS_PER_GAME,
  flipDelay: GAME_SETTINGS.FLIP_DELAY,
};

/**
 * Memory Game - Card matching game with flip animations
 * Fully compliant with universal game architecture
 */
export function MemoryGame() {
  const t = useTranslations();

  // Game settings with persistence
  const { settings, updateSettings } = useGameSettings<MemoryGameSettings>('memory', MEMORY_DEFAULT_SETTINGS);

  // Create initial game state
  const createInitialGame = useCallback((): MemoryGameType => {
    const cards = generateCards(settings.difficulty);

    return {
      id: 'memory-game',
      name: 'Memory Game',
      description: 'Match pairs of cards',
      version: '1.0.0',
      status: 'idle',
      difficulty: settings.difficulty,
      settings,
      statistics: DEFAULT_GAME_STATISTICS,
      createdAt: new Date(),
      updatedAt: new Date(),
      cards,
      flippedCards: [],
      matchedPairs: [],
      hintsRemaining: settings.maxHints,
      moves: 0,
    };
  }, [settings]);

  // Game state management
  const { game, updateGame, startGame, completeGame } = useGameState<MemoryGameType>(
    createInitialGame(),
    (result: GameResult) => {
      setShowVictory(true);
    }
  );

  // Timer
  const timer = useGameTimer();

  // UI state
  const [showVictory, setShowVictory] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [mounted, setMounted] = useState(false);

  // Prevent hydration mismatch by only rendering game board on client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Hint system
  const { highlightedCards, showHint, clearHighlight } = useHintSystem({
    cards: game.cards,
    hintsRemaining: game.hintsRemaining,
    gameStatus: game.status,
    onHintUsed: () => {
      updateGame({ hintsRemaining: game.hintsRemaining - 1 });
      setHintsUsed(prev => prev + 1);
    }
  });

  // Handle card flip
  const handleCardClick = useCallback((cardId: number) => {
    // Start game on first card flip
    if (game.status === 'idle') {
      startGame();
      timer.start();
    }

    if (game.status !== 'playing' && game.status !== 'idle') return;

    const card = game.cards.find(c => c.id === cardId);
    if (!card || card.isFlipped || card.isMatched || game.flippedCards.length >= 2) return;

    // Clear hint highlight when player clicks a highlighted card
    if (highlightedCards.includes(cardId)) {
      clearHighlight();
    }

    const newFlippedCards = [...game.flippedCards, cardId];

    // If this is the second flipped card, check for match
    if (newFlippedCards.length === 2) {
      const [firstCardId, secondCardId] = newFlippedCards;
      const firstCard = game.cards.find(c => c.id === firstCardId);
      const secondCard = game.cards.find(c => c.id === secondCardId);

      if (firstCard && secondCard && firstCard.emoji === secondCard.emoji) {
        // Match found!
        const newMatchedPairs = [...game.matchedPairs, firstCard.pairId];
        const updatedCards = game.cards.map(card => {
          if (card.id === firstCardId || card.id === secondCardId) {
            return { ...card, isFlipped: true, isMatched: true };
          }
          return { ...card, isFlipped: card.id === cardId };
        });

        // Check if game is completed
        const totalPairs = Math.floor(game.cards.length / 2);
        const gameCompleted = newMatchedPairs.length === totalPairs;

        updateGame({
          cards: updatedCards,
          flippedCards: [],
          matchedPairs: newMatchedPairs,
          moves: game.moves + 1,
        });

        if (gameCompleted) {
          timer.stop();
          completeGame({
            score: totalPairs,
            hintsUsed,
            metadata: { moves: game.moves + 1 },
          });
        }
      } else {
        // No match - flip cards back after delay
        const updatedCards = game.cards.map(card => ({
          ...card,
          isFlipped: card.id === firstCardId || card.id === secondCardId || card.id === cardId
        }));

        updateGame({
          cards: updatedCards,
          flippedCards: newFlippedCards,
          moves: game.moves + 1,
        });

        setTimeout(() => {
          updateGame({
            cards: game.cards.map(card => ({
              ...card,
              isFlipped: card.isMatched
            })),
            flippedCards: []
          });
        }, settings.flipDelay);
      }
    } else {
      // First card flipped
      const updatedCards = game.cards.map(card => ({
        ...card,
        isFlipped: card.isMatched || card.id === cardId
      }));

      updateGame({
        cards: updatedCards,
        flippedCards: newFlippedCards,
      });
    }
  }, [game, startGame, timer, updateGame, completeGame, hintsUsed, settings.flipDelay, highlightedCards, clearHighlight]);

  // Get next difficulty level
  const getNextDifficulty = useCallback((current: GameDifficulty): GameDifficulty | null => {
    const order: GameDifficulty[] = ['easy', 'medium', 'hard', 'expert'];
    const currentIndex = order.indexOf(current);
    return currentIndex < order.length - 1 ? order[currentIndex + 1] : null;
  }, []);

  // Check if difficulty can be increased
  const canIncreaseDifficulty = getNextDifficulty(settings.difficulty) !== null;

  // Handle new game
  const handleNewGame = useCallback(() => {
    const newGame = createInitialGame();
    updateGame(newGame);
    timer.reset();
    setShowVictory(false);
    setHintsUsed(0);
  }, [createInitialGame, updateGame, timer]);

  // Handle difficulty change
  const handleDifficultyChange = useCallback((difficulty: GameDifficulty) => {
    updateSettings({ difficulty });
    timer.reset();
    setShowVictory(false);
    setHintsUsed(0);
    // Trigger game recreation in next render
  }, [updateSettings, timer]);

  // Handle increase difficulty (for victory modal)
  const handleIncreaseDifficulty = useCallback(() => {
    const nextDifficulty = getNextDifficulty(settings.difficulty);
    if (nextDifficulty) {
      handleDifficultyChange(nextDifficulty);
    }
  }, [settings.difficulty, getNextDifficulty, handleDifficultyChange]);

  // Recreate game when settings change
  useEffect(() => {
    const newGame = createInitialGame();
    updateGame(newGame);
  }, [settings.difficulty]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle hint button click (blocked while a hint is already visible)
  const handleHintClick = useCallback(() => {
    if (highlightedCards.length > 0) return;
    showHint();
  }, [showHint, highlightedCards]);

  // Can flip card helper
  const canFlipCard = useCallback((cardId: number): boolean => {
    const card = game.cards.find(c => c.id === cardId);
    return Boolean(
      card &&
      !card.isFlipped &&
      !card.isMatched &&
      game.flippedCards.length < 2
    );
  }, [game.cards, game.flippedCards]);

  const totalPairs = Math.floor(game.cards.length / 2);
  const config = GAME_CONFIG[game.difficulty];

  return (
    <>
      <GameLayout
        gameId="memory"
        title={t('games.memory.title')}
        timer={timer}
        stats={{
          score: game.matchedPairs.length,
          target: totalPairs,
          moves: game.moves
        }}
        settings={
          <DifficultySelector
            difficulty={settings.difficulty}
            onDifficultyChange={handleDifficultyChange}
          />
        }
        onNewGame={handleNewGame}
        onShowRules={() => setShowRules(true)}
        extraActions={
          game.status === 'playing' && (game.hintsRemaining > 0 || highlightedCards.length > 0) ? (
            <Button
              variant="outline"
              size="sm"
              onClick={handleHintClick}
              disabled={highlightedCards.length > 0}
              className="gap-1 sm:gap-2 h-7 sm:h-9 sm:w-auto sm:px-3 px-1.5 border-warning/50 bg-warning/10 hover:bg-warning/20 text-warning-foreground dark:text-warning disabled:opacity-50"
              data-testid="memory-hint-button"
              data-hint-active={highlightedCards.length > 0 || undefined}
            >
              <Lightbulb className="h-3.5 w-3.5 sm:h-4 sm:w-4 fill-warning/30" />
              <span className="hidden sm:inline">{t('games.memory.hint')} ({game.hintsRemaining})</span>
              <span className="sm:hidden text-[10px] font-bold" data-testid="memory-hints-remaining">{game.hintsRemaining}</span>
            </Button>
          ) : undefined
        }
      >
        {/* Game Board with landscape support */}
        <div className="flex flex-col items-center justify-center w-full h-full landscape:flex-row landscape:gap-4" data-testid="memory-game-area">
          {mounted ? (
            <GameBoard
              cards={game.cards}
              difficulty={game.difficulty}
              highlightedCards={highlightedCards}
              onCardClick={handleCardClick}
              canFlipCard={canFlipCard}
            />
          ) : (
            <div className="text-muted-foreground" data-testid="memory-loading">{t('loading')}</div>
          )}
        </div>
      </GameLayout>

      {/* Victory Modal */}
      <GameVictoryModal
        isOpen={showVictory}
        result={{
          gameId: 'memory',
          status: 'won',
          score: game.matchedPairs.length,
          time: timer.elapsedTime,
          difficulty: game.difficulty,
          hintsUsed,
          completedAt: new Date(),
          metadata: {
            moves: game.moves,
            totalPairs
          },
        }}
        onClose={() => setShowVictory(false)}
        onNewGame={() => {
          setShowVictory(false);
          handleNewGame();
        }}
        onIncreaseDifficulty={() => {
          setShowVictory(false);
          handleIncreaseDifficulty();
        }}
        canIncreaseDifficulty={canIncreaseDifficulty}
        onChangeDifficulty={() => {
          setShowVictory(false);
        }}
        customStats={
          <div className="text-center" data-testid="memory-victory-custom-stats">
            <p className="text-sm text-muted-foreground" data-testid="memory-victory-moves-summary">
              {t('games.memory.completedInMoves', { moves: game.moves })}
            </p>
            <p className="text-sm text-muted-foreground" data-testid="memory-victory-pairs-summary">
              {t('games.memory.matchedPairs', { matched: game.matchedPairs.length, total: totalPairs })}
            </p>
          </div>
        }
      />

      {/* Rules Modal */}
      <GameRulesModal
        gameTitle={t('games.memory.title')}
        isOpen={showRules}
        onClose={() => setShowRules(false)}
        rulesContent={
          <div className="space-y-4" data-testid="memory-rules-content">
            {/* Objective */}
            <div data-testid="memory-rules-objective">
              <h4 className="font-semibold text-foreground mb-2">{t('games.memory.rules.objective')}</h4>
              <p className="text-sm text-muted-foreground">{t('games.memory.rules.objectiveText')}</p>
            </div>

            {/* How to Play */}
            <div data-testid="memory-rules-how-to-play">
              <h4 className="font-semibold text-foreground mb-2">{t('games.memory.rules.howToPlay')}</h4>
              <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                <li>{t('games.memory.rules.step1')}</li>
                <li>{t('games.memory.rules.step2')}</li>
                <li>{t('games.memory.rules.step3')}</li>
                <li>{t('games.memory.rules.step4')}</li>
              </ol>
            </div>

            {/* Difficulty */}
            <div data-testid="memory-rules-difficulty">
              <h4 className="font-semibold text-foreground mb-2">{t('games.memory.rules.difficulty')}</h4>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li><strong>{t('games.difficulty.easy.name')}:</strong> {t('games.memory.rules.easyDesc')}</li>
                <li><strong>{t('games.difficulty.medium.name')}:</strong> {t('games.memory.rules.mediumDesc')}</li>
                <li><strong>{t('games.difficulty.hard.name')}:</strong> {t('games.memory.rules.hardDesc')}</li>
                <li><strong>{t('games.difficulty.expert.name')}:</strong> {t('games.memory.rules.expertDesc')}</li>
              </ul>
            </div>

            {/* Tips */}
            <div data-testid="memory-rules-tips">
              <h4 className="font-semibold text-foreground mb-2">{t('games.memory.rules.tips')}</h4>
              <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                <li>{t('games.memory.rules.tip1')}</li>
                <li>{t('games.memory.rules.tip2')}</li>
                <li>{t('games.memory.rules.tip3')}</li>
              </ul>
            </div>
          </div>
        }
      />
    </>
  );
}
